import {
  ImportModulePreflightResult, ImportModulePreflightRow, ImportModulePreflightSharedFile,
} from '../../../core/definitions/import-module';
import type {MediaOnMissing} from '../import-payload';
import {ImportView} from '../step-bar/import-steps';

/** Which kind of event card an event-group key belongs to. */
export type EventGroupKind = ImportModulePreflightResult['event_groups'][number]['kind'];

/** A result's block-level errors, split by whether `/run` refuses them as
 * well (`refusing`) or not (`notRefusing`). */
export interface PreflightErrorSplit {
  refusing: ImportModulePreflightResult['errors'];
  notRefusing: ImportModulePreflightResult['errors'];
}

/** What a warning is about, which picks its text and where it is fixed. */
export type WarningKind =
  'field' | 'concept' | 'place' | 'party' | 'location' | 'kulturnav' | 'card' | 'media'
  | 'id_collision' | 'id_invalid' | 'created_at' | 'created_by';

/** One warning on one row, with the label the panel shows for it. */
export interface RowWarning {
  kind: WarningKind;
  // A place, party, location, card or media warning's `reason` /
  // `status`, `shared` for a matched row that links a file other rows
  // name too; '' otherwise.
  reason: string;
  label: string;
  // The value the warning is about; '' for a card warning, whose member
  // is blank.
  value: string;
  // A field warning's reasons: translation keys, or a raw keyword where
  // there is no key; [] otherwise.
  reasons: string[];
  // The card the value feeds (`event_key`): an event card or a flat group
  // card; '' for the object's own value or a warning that names no card.
  eventKey: string;
  // The sub-block of an event card the value feeds (`sub_block`); '' for
  // a value from no sub-block.
  subBlock: string;
  // `'media'` for a value written onto the linked media, `null` otherwise.
  on: 'media' | null;
  // A media warning's files that other rows name too, with those rows;
  // [] otherwise.
  shared: ImportModulePreflightSharedFile[];
}

/** Where a warning type is fixed: a step view to open, or `place` for a
 * place that has to exist before the run, which no step fixes. */
export type WarningFix =
  {kind: 'view'; view: ImportView; labelKey: string}
  | {kind: 'place'; labelKey: string};

/** The rows that share one warning message. `warning` is the first of
 * them, for the text; a kind whose value is the row's own (an
 * identification number, a file name, a date, a rejected free-text value)
 * is grouped without its value. */
export interface WarningGroup {
  typeKey: string;
  warning: RowWarning;
  withValue: boolean;
  rows: string[];
  fix: WarningFix | null;
}

/** Names a result's values by the card they feed: an event card, whose
 * key `event_groups` lists, or a flat group card, whose key it does not. */
export interface CardLabels {
  /** The title of the event card an event-group key belongs to, as the
   * card shows it; `null` for a key `event_groups` does not carry, or no
   * card has. */
  title(key: string): string | null;
  /** A value's label, prefixed with its card's title when it feeds one
   * ("Historikkhendelse 1 · Fotografering · Navn", "Materiale 2 ·
   * Materiale"): the member's field label alone reads as a field of the
   * object, and does not tell two cards of one kind apart. A value from a
   * sub-block the card names (`subBlock`) has the block's name between
   * the card's title and the field label ("Historikkhendelse 1 · Egennavn
   * 2 · Egennavn"), which tells two blocks of one kind on a card apart. */
  label(label: string, eventKey?: string | null, subBlock?: string | null): string;
}

/** The card labels of one result. An event card's kind comes from
 * `event_groups` (the first group with that key); a key it does not list
 * is resolved as a flat group card's, with kind `null`. A sub-block's
 * name comes from `resolveSubBlockTitle`, `null` for a block the card
 * does not name. Each key is resolved once, and each label built once per
 * key and sub-block. */
export function cardLabels(
  result: ImportModulePreflightResult, resolveTitle: (key: string, kind: EventGroupKind | null) => string | null,
  resolveSubBlockTitle: (cardKey: string, subBlock: string) => string | null = () => null,
): CardLabels {
  const kinds = new Map<string, EventGroupKind>();
  for (const g of result.event_groups) {
    if (!kinds.has(g.key)) {
      kinds.set(g.key, g.kind);
    }
  }
  const titles = new Map<string, string | null>();
  const labels = new Map<string, string>();
  const cardTitle = (key: string): string | null => {
    if (!titles.has(key)) {
      titles.set(key, resolveTitle(key, kinds.get(key) ?? null));
    }
    return titles.get(key)!;
  };
  const title = (key: string): string | null => kinds.has(key) ? cardTitle(key) : null;
  const label = (text: string, eventKey?: string | null, subBlock?: string | null): string => {
    if (!eventKey) {
      return text;
    }
    const memoKey = JSON.stringify([eventKey, subBlock ?? '', text]);
    let labelled = labels.get(memoKey);
    if (labelled === undefined) {
      const titleText = cardTitle(eventKey);
      const blockTitle = titleText && subBlock ? resolveSubBlockTitle(eventKey, subBlock) : null;
      labelled = titleText ? [titleText, blockTitle, text].filter(Boolean).join(' · ') : text;
      labels.set(memoKey, labelled);
    }
    return labelled;
  };
  return {title, label};
}

/** A value's label as Kontroll shows it: one written onto the linked
 * media (`on`) under `mediaTitle` («Bilde · Fotograf»), any other as
 * `labels` gives it. */
export function valueLabel(labels: CardLabels, mediaTitle: string) {
  return (label: string, eventKey?: string | null, on?: 'media' | null, subBlock?: string | null): string =>
    on === 'media' ? `${mediaTitle} · ${label}` : labels.label(label, eventKey, subBlock);
}

// The media matches that are a warning on their row: no media, several
// where a row with several files is skipped, files all or some of which
// are linked already, or a file other rows name too where such a row is
// skipped; `unknown` means no match was looked up.
const MEDIA_WARNING_STATUSES: ReadonlySet<string> =
  new Set(['unmatched', 'ambiguous', 'already_linked', 'partly_linked', 'shared_file']);

// A row warning entry on a value: where the value goes and what it is.
// A card warning has no `value`.
interface WarnedValue {
  label: string;
  value?: string;
  event_key?: string;
  sub_block?: string;
  on?: 'media';
}

/** A row's warnings in the order the row lists them, each by the label
 * `labelOf` gives it. A party name is a warning only where it matches no
 * record and the run creates none (`unmatched`). A field, concept, place,
 * party, location or KulturNav warning on a value that feeds a card
 * (`event_key`) names that card and carries its key, and one on a value
 * from a sub-block of it (`sub_block`) that block's key; a field or party
 * warning on a value written onto the linked media passes its `on`; a
 * card warning names the card the run writes nothing for on the row, by
 * the required member the row leaves blank. A media warning carries the
 * row's files other rows name too, and a matched row that links such a
 * file is warned of as `shared`. */
export function rowWarnings(
  row: ImportModulePreflightRow,
  labelOf: (label: string, eventKey?: string, on?: 'media', subBlock?: string) => string,
): RowWarning[] {
  const onValue = (kind: WarningKind, w: WarnedValue, reason = '', reasons: string[] = []): RowWarning => ({
    kind, reason, label: labelOf(w.label, w.event_key, w.on, w.sub_block), value: w.value ?? '', reasons,
    eventKey: w.event_key ?? '', subBlock: w.sub_block ?? '', on: w.on ?? null, shared: [],
  });
  const onRow = (
    kind: WarningKind, value: string, reason = '', shared: ImportModulePreflightSharedFile[] = [],
  ): RowWarning => ({kind, reason, label: '', value, reasons: [], eventKey: '', subBlock: '', on: null, shared});
  const warnings: RowWarning[] = [
    ...row.field_warnings.map(w => onValue('field', w, '', w.reasons)),
    ...row.concept_warnings.filter(w => w.reason === 'unresolved').map(w => onValue('concept', w)),
    ...row.place_warnings.map(w => onValue('place', w, w.reason)),
    ...row.party_warnings.filter(w => w.reason === 'unmatched').map(w => onValue('party', w, w.reason)),
    ...row.location_warnings.map(w => onValue('location', w, w.reason)),
    ...row.kulturnav_warnings.filter(w => w.reason === 'invalid_uuid').map(w => onValue('kulturnav', w)),
    ...row.group_warnings.map(w => onValue('card', w, w.reason)),
  ];
  const media = row.media;
  if (media && MEDIA_WARNING_STATUSES.has(media.status)) {
    warnings.push(onRow('media', media.value, media.status, media.shared));
  } else if (media?.status === 'matched' && media.shared.length) {
    warnings.push(onRow('media', media.value, 'shared', media.shared));
  }
  if (row.id_number?.status === 'collision') {
    warnings.push(onRow('id_collision', row.id_number.value));
  } else if (row.id_number?.status === 'invalid') {
    warnings.push(onRow('id_invalid', row.id_number.value));
  }
  const created = row.created_revision;
  if (created?.created_at?.status === 'invalid') {
    warnings.push(onRow('created_at', created.created_at.value));
  }
  if (created?.created_by?.status === 'unresolved') {
    warnings.push(onRow('created_by', created.created_by.value));
  }
  return warnings;
}

// Kinds whose value differs per row, so that grouping by value would give
// each row a group of its own.
const ROW_OWN_VALUE_KINDS: ReadonlySet<WarningKind> =
  new Set<WarningKind>(['field', 'media', 'id_collision', 'id_invalid', 'created_at']);

/** Whether a value holds no letter and no digit ("+", "-", "?"). */
export function isPunctuationOnly(value: string): boolean {
  return value.trim() !== '' && !/[\p{L}\p{N}]/u.test(value);
}

/** Whether a warning of this kind is grouped with its value. */
export function groupsWithValue(kind: WarningKind): boolean {
  return !ROW_OWN_VALUE_KINDS.has(kind);
}

/** The key rows with the same warning message share. */
export function warningTypeKey(w: RowWarning): string {
  return JSON.stringify([w.kind, w.reason, w.eventKey, w.label, w.reasons, groupsWithValue(w.kind) ? w.value : null]);
}

/** Where a warning is fixed: a value with no letter or digit on the
 * blank-values tab, an unresolved list value in the value review, a place
 * or location outside the module, a media match on the source step, and
 * anything else, a party name and a card left short among them, in the
 * mapping. A row whose files, all or some, other objects link already is
 * fixed nowhere in the module: `null`. */
export function warningFix(w: RowWarning): WarningFix | null {
  if (groupsWithValue(w.kind) && isPunctuationOnly(w.value)) {
    return {kind: 'view', view: 'tomme-verdier', labelKey: 'TRANS__IMPORT__PREFLIGHT_FIX_BLANK'};
  }
  switch (w.kind) {
    case 'concept':
      return {kind: 'view', view: 'listeverdier', labelKey: 'TRANS__IMPORT__PREFLIGHT_FIX_VALUES'};
    case 'place':
    case 'location':
      return {kind: 'place', labelKey: 'TRANS__IMPORT__PREFLIGHT_FIX_PLACE'};
    case 'media':
      return w.reason === 'already_linked' || w.reason === 'partly_linked'
        ? null : {kind: 'view', view: 'kilde', labelKey: 'TRANS__IMPORT__PREFLIGHT_FIX_SOURCE'};
    default:
      return {kind: 'view', view: 'feltkoblinger', labelKey: 'TRANS__IMPORT__PREFLIGHT_FIX_MAPPING'};
  }
}

/** The warning types across `rows`, each with the rows that carry it,
 * most rows first; types with as many rows keep the order they first
 * appear in. A row counts once per type. */
export function groupWarnings(rows: {row: string; warnings: RowWarning[]}[]): WarningGroup[] {
  const groups = new Map<string, WarningGroup>();
  for (const {row, warnings} of rows) {
    for (const warning of warnings) {
      const typeKey = warningTypeKey(warning);
      let group = groups.get(typeKey);
      if (!group) {
        group = {typeKey, warning, withValue: groupsWithValue(warning.kind), rows: [], fix: warningFix(warning)};
        groups.set(typeKey, group);
      }
      if (group.rows.at(-1) !== row) {
        group.rows.push(row);
      }
    }
  }
  return [...groups.values()].sort((a, b) => b.rows.length - a.rows.length);
}

/** A placement card's outcome over the rows the run creates: the rows it
 * gives a placement, and the rows it gives none. */
export interface PlacementOutlook {
  key: string;
  given: number;
  missing: number;
}

/** A field's values the run skips, by the rows that hold one. `eventKey`
 * names the card the field belongs to (an event card or a flat group
 * card), `null` for the object's own field or a warning that names no
 * card; `subBlock` the sub-block of that card the value feeds, `null` for
 * a value outside any sub-block; `on` is `'media'` for a field of the
 * linked media, `null` for any other. */
export interface SkippedFieldOutlook {
  label: string;
  eventKey: string | null;
  subBlock: string | null;
  on: 'media' | null;
  rows: number;
}

// The order the outlook lists the skip reasons in.
const ROW_SKIP_ORDER = [
  'media_already_linked', 'media_partly_linked', 'media_shared_file', 'media_ambiguous', 'media_unmatched',
  'id_collision', 'id_invalid',
] as const;

/** Why the run creates nothing for a row: its media files are all linked
 * already, or some of them, a file of its is one other rows name too
 * where such a row is skipped, several media files match where such a row
 * is skipped, none matches (unless the run creates such rows without
 * media), or its identification number exists already or has a length
 * the field does not take. */
export type RowSkipReason = typeof ROW_SKIP_ORDER[number];

/** The rows skipped for one reason. */
export interface RowSkipOutlook {
  reason: RowSkipReason;
  rows: number;
}

/** What a preflight says a run over the same rows does: the objects it
 * creates, the rows it skips and why, and on the rows it creates, the
 * placements it gives, the values it skips and the cards other than
 * placement cards it writes nothing for (`skippedCards`, counted once
 * per card and row). */
export interface RunOutlook {
  created: number;
  skippedRows: RowSkipOutlook[];
  placements: PlacementOutlook[];
  skipped: SkippedFieldOutlook[];
  skippedCards: number;
}

/** Why the run creates nothing for `row`, `null` when it creates it. The
 * media match is checked before the object is written, so it goes first. */
export function rowSkipReason(row: ImportModulePreflightRow, onMissing: MediaOnMissing): RowSkipReason | null {
  switch (row.media?.status) {
    case 'already_linked':
      return 'media_already_linked';
    case 'partly_linked':
      return 'media_partly_linked';
    case 'shared_file':
      return 'media_shared_file';
    case 'ambiguous':
      return 'media_ambiguous';
    case 'unmatched':
      if (onMissing !== 'create_without_media') {
        return 'media_unmatched';
      }
  }
  if (row.id_number?.status === 'collision') {
    return 'id_collision';
  }
  return row.id_number?.status === 'invalid' ? 'id_invalid' : null;
}

/**
 * What a run over the checked rows does. A row is created unless
 * `rowSkipReason` gives a reason. On the created rows, a placement card
 * gives a row a placement when the row has a Location for it and no
 * `location_warnings` entry for it (a code that matches no Location or
 * several). A card that fires only on some rows has a Location on the
 * rows whose `events` list it; a card that fires on every row, on the
 * rows that compose a value for its Location member
 * (`placementLocationFieldId`), since the placement is not written
 * without one. A skipped value is an unresolved list value, a value that
 * fails validation, a Place name that matches no Place or several, a
 * fixed Place that is no Place, a party name that matches no record and
 * that the run does not create, or a KulturNav id that is no UUID. A
 * skipped card is one a row's `group_warnings` name other than a
 * placement card, whose loss `placements` counts.
 */
export function runOutlook(
  result: ImportModulePreflightResult, isPlacement: (key: string) => boolean, onMissing: MediaOnMissing,
  placementLocationFieldId: string | null,
): RunOutlook {
  const skipCounts = new Map<RowSkipReason, number>();
  const created: ImportModulePreflightRow[] = [];
  for (const row of result.rows) {
    const reason = rowSkipReason(row, onMissing);
    if (reason) {
      skipCounts.set(reason, (skipCounts.get(reason) ?? 0) + 1);
    } else {
      created.push(row);
    }
  }
  const skippedRows = ROW_SKIP_ORDER
    .filter(reason => skipCounts.has(reason))
    .map(reason => ({reason, rows: skipCounts.get(reason)!}));
  const createdCount = result.rows_previewed - (result.rows.length - created.length);
  const placementGroups = result.event_groups.filter(g => g.kind === 'admin' && isPlacement(g.key));
  const placements = placementGroups
    .map(g => {
      const given = created.filter(row =>
        (g.always_fires
          ? row.composed.some(entry => entry.event_key === g.key && entry.target_field === placementLocationFieldId)
          : row.events.includes(g.key))
        && !row.location_warnings.some(w => w.event_key === g.key)).length;
      return {key: g.key, given, missing: createdCount - given};
    });
  const skippedValues = new Map<string, {field: SkippedField; rows: Set<string>}>();
  for (const row of created) {
    for (const field of skippedFields(row)) {
      const key = JSON.stringify([field.eventKey, field.subBlock, field.on, field.label]);
      if (!skippedValues.has(key)) {
        skippedValues.set(key, {field, rows: new Set()});
      }
      skippedValues.get(key)!.rows.add(row.row);
    }
  }
  const skipped = [...skippedValues.values()]
    .map(({field, rows}) => ({...field, rows: rows.size}))
    .sort((a, b) => b.rows - a.rows);
  const placementKeys = new Set(placementGroups.map(g => g.key));
  const skippedCards = created.reduce((sum, row) => sum + new Set(
    row.group_warnings.map(w => w.event_key).filter(key => !placementKeys.has(key))).size, 0);
  return {created: createdCount, skippedRows, placements, skipped, skippedCards};
}

type SkippedField = Pick<SkippedFieldOutlook, 'label' | 'eventKey' | 'subBlock' | 'on'>;

// The warning kinds whose value the run skips on a row it creates, in the
// order a row's skipped values are counted.
const SKIPPED_VALUE_KINDS: readonly WarningKind[] = ['concept', 'field', 'place', 'party', 'kulturnav'];

function skippedFields(row: ImportModulePreflightRow): SkippedField[] {
  const warnings = rowWarnings(row, label => label);
  return SKIPPED_VALUE_KINDS
    .flatMap(kind => warnings.filter(w => w.kind === kind))
    .map(w => ({label: w.label, eventKey: w.eventKey || null, subBlock: w.subBlock || null, on: w.on}));
}
